import { useCallback, useEffect, useState } from 'react';

import { useFocusEffect } from 'expo-router';

import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

/**
 *
 * @param loyaltyId - The loyalty ID of the user
 * @param currentPoints - The current points of the user
 * @returns The last seen loyalty points, the tab focused state, and the function
 * to update the stored last seen points
 *
 */
export const useLastSeenLoyaltyPoints = (currentPoints: number) => {
  const [lastSeenLoyaltyPoints, setLastSeenLoyaltyPoints] = useState<number | null>(null);
  const [isTabFocused, setIsTabFocused] = useState(false);
  const { loyaltyUser } = useLoyaltyUser();
  const loyaltyId = loyaltyUser?.id;

  // Store and read the last seen points, used to animate the progress bar (start value of the animation)
  const getStoredLastSeenPoints = useCallback(() => {
    try {
      return LocalStorage.getItem(StorageKeys.LAST_SEEN_LOYALTY_POINTS) || {};
    } catch (error) {
      return {};
    }
  }, []);

  const updateStoredLastSeenPoints = useCallback(
    (loyaltyId: string, points: number) => {
      try {
        const stored = getStoredLastSeenPoints();
        stored[loyaltyId] = points;
        LocalStorage.setItem(StorageKeys.LAST_SEEN_LOYALTY_POINTS, stored);
      } catch (error) {
        LocalStorage.setItem(StorageKeys.LAST_SEEN_LOYALTY_POINTS, {});
      }
    },
    [getStoredLastSeenPoints]
  );

  const refreshLastSeenLoyaltyPoints = useCallback(() => {
    if (!isTabFocused || !loyaltyId) {
      return;
    }
    const stored = getStoredLastSeenPoints();
    const storedPoints = stored[loyaltyId] || 0;

    setLastSeenLoyaltyPoints(storedPoints);

    // Only update stored points if currentPoints is different from stored points
    // and currentPoints is not 0 (to avoid overwriting with 0 on initial load)
    if (currentPoints !== storedPoints) {
      updateStoredLastSeenPoints(loyaltyId, currentPoints);
    }
  }, [isTabFocused, loyaltyId, getStoredLastSeenPoints, currentPoints, updateStoredLastSeenPoints]);

  useFocusEffect(
    useCallback(() => {
      setIsTabFocused(true);

      return () => {
        setIsTabFocused(false);
      };
    }, [])
  );

  // Load last seen points when user changes
  useEffect(() => {
    refreshLastSeenLoyaltyPoints();
  }, [refreshLastSeenLoyaltyPoints]);

  return {
    lastSeenLoyaltyPoints,
    isTabFocused,
    refreshLastSeenLoyaltyPoints,
  };
};
